import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  CheckoutItem,
  CheckoutProduct,
  CheckoutResponse,
  OmsOrder,
  OmsOrdersResponse,
  Order,
  OrderItem,
  OrderStatus,
  ORDER_STEPS,
  ORDER_STEP_LABELS,
  StatusTimeline,
  StockCreditCheckResponse,
} from '../models';
import { API_BASE_URL } from '../tokens/api-base-url.token';

/** Coerce a raw backend status string onto one of our known milestones. */
function normalizeStatus(raw?: string): OrderStatus {
  const map: Record<string, OrderStatus> = {
    created: 'created',
    stockAssigned: 'stockAssigned',
    picked: 'stockAssigned',
    packed: 'packed',
    readyToDispatch: 'readyToDispatch',
    readyToDispatched: 'readyToDispatch',
    dispatched: 'readyToDispatch',
    inProgress: 'inProgress',
    delivered: 'delivered',
  };
  return (raw && map[raw]) || 'created';
}

/** "3 Aug 2026" — empty string when the date is missing/invalid. */
function formatDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Keep only the milestones the backend actually reported, in pipeline order. */
function toTimeline(raw: OmsOrder['statusTimeline']): StatusTimeline | null {
  if (!raw) return null;
  const timeline: StatusTimeline = {};
  for (const step of ORDER_STEPS) {
    const entry = raw[step];
    if (entry?.date) timeline[step] = { date: entry.date };
  }
  return Object.keys(timeline).length ? timeline : null;
}

/** Map a raw OMS order onto the view model the orders page renders. */
function toOrder(o: OmsOrder): Order {
  const items: OrderItem[] = (o.orderDetails?.items ?? []).map((it) => ({
    name: it.name ?? 'Item',
    quantity: it.quantity ?? 0,
    price: it.price ?? 0,
  }));
  const status = normalizeStatus(o.orderStatus);
  const customerName = [o.customerDetails?.firstName, o.customerDetails?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();

  return {
    id: o.orderId ?? String(o.serialNo ?? ''),
    date: formatDate(o.created),
    units: items.reduce((sum, it) => sum + it.quantity, 0),
    total: items.reduce((sum, it) => sum + it.price * it.quantity, 0),
    status,
    statusLabel: ORDER_STEP_LABELS[status],
    customerName: customerName || '—',
    city: o.pickUpCity?.city ?? o.customerDetails?.location?.city ?? '',
    items,
    statusTimeline: toTimeline(o.statusTimeline),
  };
}

/** Order history + order placement. */
@Injectable({ providedIn: 'root' })
export class OrdersService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  /** Fetch OMS orders created within [startDate, endDate] for a store, mapped to view models. */
  getOrders(startDate: number, endDate: number, storeId: string): Observable<Order[]> {
    const params = new HttpParams({
      fromObject: {
        skip: 0,
        limit: 20,
        startDate,
        endDate,
        dateField: 'created',
        deliveryCenterIds: storeId,
        sortByFieldsStr: 'created,-1',
      },
    });
    return this.http
      .get<OmsOrdersResponse>(`${this.baseUrl}/api/sok/oms/orders`, { params })
      .pipe(map((res) => (res?.data ?? []).map(toOrder)));
  }

  /** Validate stock + credit for the basket before checkout. */
  checkStockAndCredit(
    storeId: string,
    products: CheckoutProduct[],
  ): Observable<StockCreditCheckResponse> {
    return this.http.post<StockCreditCheckResponse>(
      `${this.baseUrl}/api/sok/oms/checkStockAndCredit`,
      { storeId, products },
    );
  }

  /** Raise the order against the warehouse. */
  checkout(storeId: string, items: CheckoutItem[]): Observable<CheckoutResponse> {
    return this.http.post<CheckoutResponse>(`${this.baseUrl}/api/sok/oms/orders/checkOut`, {
      storeId,
      orderType: 'pickUp',
      orderSource: 'wms',
      orderDetails: { items },
      payment: { status: 'notPaid' },
    });
  }
}
